import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Landmark, Users, MapPin, Home as HomeIcon, Vote } from "lucide-react";
import { useLang } from "@/i18n";
import { useSite } from "@/context/SiteContext";
import api from "@/api";

export default function Home() {
  const { t, lang } = useLang();
  const { site } = useSite();
  const tagline = site?.[`tagline_${lang}`] || t.tagline;
  const heroTitle = site?.[`hero_title_${lang}`] || t.hero_title;
  const heroSubtitle = site?.[`hero_subtitle_${lang}`] || t.hero_subtitle;
  const [stats, setStats] = useState({ members: 0, districts: 0, villages: 0, levels: 5 });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const levels = [
    { icon: Landmark, label: t.level_state, color: "bg-emerald-800" },
    { icon: MapPin, label: t.level_district, color: "bg-teal-700" },
    { icon: Vote, label: t.level_block, color: "bg-amber-600" },
    { icon: Users, label: t.level_city, color: "bg-blue-700" },
    { icon: HomeIcon, label: t.level_village, color: "bg-green-700" },
  ];

  const statItems = [
    { value: stats.members, label: t.stats_members },
    { value: stats.districts, label: t.stats_districts },
    { value: stats.villages, label: t.stats_villages },
    { value: stats.levels, label: t.stats_levels },
  ];

  return (
    <div data-testid="home-page">
      <section className="relative bg-emerald-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #D97706 0%, transparent 40%), radial-gradient(circle at 80% 70%, #E65100 0%, transparent 40%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="fade-in-up text-amber-400 font-semibold tracking-widest text-sm uppercase mb-4">{tagline}</p>
              <h1 className="fade-in-up stagger-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
                {heroTitle}
              </h1>
              <p className="fade-in-up stagger-2 text-base sm:text-lg text-emerald-100 leading-relaxed mb-8">
                {heroSubtitle}
              </p>
              <div className="fade-in-up stagger-3 flex flex-wrap gap-4">
                <Link to="/membership" data-testid="hero-join-btn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-amber-600 hover:bg-amber-700 font-bold transition-all hover:scale-105">
                  {t.join_now} <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/donation" data-testid="hero-donate-btn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-amber-500 text-amber-400 hover:bg-amber-600 hover:text-white font-bold transition-all">
                  {t.donate_now}
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl scale-110" />
                <img src="/logo.jpeg" alt="Samata Party - Flaming Torch"
                  className="relative w-72 h-72 rounded-full bg-white object-contain p-6 shadow-2xl" data-testid="hero-logo" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-16" data-testid="stats-section">
            {statItems.map((s, i) => (
              <div key={i} className="text-center bg-emerald-900/60 rounded-2xl py-6 px-4 border border-emerald-800">
                <div className="font-heading text-3xl sm:text-4xl font-extrabold text-amber-400" data-testid={`stat-value-${i}`}>{s.value}</div>
                <div className="text-xs sm:text-sm text-emerald-200 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-950 mb-4">{t.levels_title}</h2>
          <p className="text-slate-600">{t.levels_subtitle}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-testid="levels-grid">
          {levels.map((l, i) => (
            <div key={i} className="bg-white rounded-2xl border border-emerald-100 p-6 text-center shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className={`w-14 h-14 ${l.color} rounded-2xl mx-auto flex items-center justify-center mb-4`}>
                <l.icon className="w-7 h-7 text-white" />
              </div>
              <div className="font-heading font-semibold text-emerald-950">{l.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-emerald-950 text-center mb-12">{t.leaders_title}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 max-w-6xl mx-auto" data-testid="leaders-section">
            {[
              { img: "/george-fernandes.jpeg", name: "Late Shri George Fernandes", role: `${t.founder} | Former Defence Minister of India` },
              { img: "/brahmanand-mandal.jpg", name: "Late Shri Brahmanand Mandal", role: `${t.saviour} | Ex-MP, Munger` },
              { img: "/uday-mandal.jpg", name: "Shri Uday Mandal", role: "National President" },
              { img: "/trunesh-deolekar.jpg", name: "Shri Trunesh Deolekar", role: lang === "en" ? "State President, Maharashtra" : "प्रदेश अध्यक्ष, महाराष्ट्र" },
              { img: "/rajni-deolekar.jpg", name: "Smt. Rajni Deolekar", role: lang === "en" ? "State President, Maharashtra Ladies Wing" : "प्रदेश अध्यक्ष, महाराष्ट्र महिला विंग" },
            ].map((l, i) => (
              <div key={i} className="text-center group">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-amber-500 shadow-lg group-hover:scale-105 transition-transform">
                  <img src={l.img} alt={l.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-heading font-bold text-emerald-950 mt-4">{l.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{l.role}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/about" data-testid="home-about-cta"
              className="inline-flex items-center gap-2 text-emerald-800 font-semibold hover:text-amber-600 transition-colors">
              {t.home_about_cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-8">{t.not_member_cta}</h2>
          <Link to="/membership" data-testid="cta-join-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-amber-600 hover:bg-amber-700 font-bold transition-all hover:scale-105">
            {t.join_now} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
